/**
 * BaseBrowserAdapter - Generic base class for all entity browser adapters
 *
 * This class provides a reusable foundation for implementing browser adapters
 * for any entity type (users, boards, items, etc.). It implements the
 * IBrowserAdapter interface with sensible defaults that can be overridden
 * by concrete implementations.
 *
 * Key Features:
 * - Generic type parameter for any entity
 * - Built-in search and filtering
 * - Single/multi selection support
 * - Focus management
 * - Optional hierarchical navigation
 * - Pagination support
 * - Loading and error states
 *
 * @example
 * ```typescript
 * class UserBrowserAdapter extends BaseBrowserAdapter<User> {
 *   protected async loadItems(): Promise<User[]> {
 *     return await userApi.getUsers();
 *   }
 *
 *   protected filterItem(user: User, query: string): boolean {
 *     return user.name.toLowerCase().includes(query.toLowerCase());
 *   }
 * }
 * ```
 */

import { reactive, toRefs } from 'vue';
import type {
  IBrowserAdapter,
  BrowserContext,
  IItemConfig,
  IBrowserState,
} from '../types/browser-interfaces';
import type { ModalFilters } from '../types/filter-types';

export abstract class BaseBrowserAdapter<T> implements IBrowserAdapter<T> {
  // State management - now reactive!
  public state: IBrowserState<T>;
  protected reactiveState: IBrowserState<T>;
  protected context: BrowserContext;
  public readonly itemConfig: IItemConfig<T>;

  // Selection mode
  protected selectionMode: 'single' | 'multi';

  // Independent selection tracking (persists across navigation)
  // This ensures selections are maintained even when items are not in current view
  // Protected to allow subclass access for debugging/testing
  protected selectedIds: Set<string> = new Set();
  protected selectedItemsCache: Map<string, T> = new Map();

  // Hierarchical navigation state
  protected navigationStack: Array<string | number | null> = [];
  protected currentParent: string | number | null = null;
  protected pendingNavigationTarget: string | number | null | undefined =
    undefined;

  // Pagination
  protected pageSize: number = 50;
  protected loadMoreThreshold: number = 10;

  /**
   * Constructor
   * @param itemConfig - Configuration for how to display and interact with items
   * @param initialContext - Optional initial context for the browser
   */
  constructor(
    itemConfig: IItemConfig<T>,
    initialContext?: Partial<BrowserContext>
  ) {
    this.itemConfig = itemConfig;

    // Initialize default context
    this.context = {
      mode: initialContext?.mode || 'single',
      searchQuery: initialContext?.searchQuery || '',
      includeArchived: initialContext?.includeArchived || false,
      parentId: initialContext?.parentId,
      filters: initialContext?.filters || {},
    };

    this.selectionMode = this.context.mode;

    // Initialize state with reactive wrapper
    const initialState: IBrowserState<T> = {
      items: [],
      filteredItems: [],
      selectedItems: [],
      focusedItem: null,
      searchQuery: '',
      isLoading: false,
      error: null,
      hasMore: false,
      page: 1,
    };

    // Create reactive state
    this.reactiveState = reactive(initialState) as IBrowserState<T>;

    // Keep the same reference for internal use
    this.state = this.reactiveState;
  }

  /**
   * Initialize the browser with data
   * Subclasses should override loadItems() to provide data
   */
  async initialize(context?: BrowserContext): Promise<void> {
    if (context) {
      this.context = { ...this.context, ...context };
      this.selectionMode = context.mode || this.selectionMode;
    }

    this.state.isLoading = true;
    this.state.error = null;

    try {
      // Load initial items
      const items = await this.loadItems();
      this.state.items = items;

      // Apply initial filtering if search query exists
      if (this.context.searchQuery) {
        this.setSearchQuery(this.context.searchQuery);
      } else {
        this.state.filteredItems = this.applyFilters(items);
      }

      // Don't auto-focus first item on initialization
      // Focus will be set when user starts keyboard navigation
      this.state.focusedItem = null;

      // Check if there are more items to load
      this.state.hasMore = await this.checkHasMore();
    } catch (error) {
      this.state.error =
        error instanceof Error ? error.message : 'Failed to load items';
      console.error('Failed to initialize browser:', error);
    } finally {
      this.state.isLoading = false;
    }
  }

  /**
   * Update the browser context and refresh if needed
   * @param context Partial context to update
   * @param refresh Whether to refresh items after context update
   */
  async setContext(
    context: Partial<BrowserContext>,
    refresh: boolean = true
  ): Promise<void> {
    const oldContext = { ...this.context };
    this.context = { ...this.context, ...context };

    // Check if mode changed
    if (context.mode && context.mode !== this.selectionMode) {
      this.selectionMode = context.mode;
      // Clear selection when switching modes
      this.clearSelection();
    }

    // Check if we need to refresh based on what changed
    const needsRefresh =
      refresh &&
      (context.includeArchived !== oldContext.includeArchived ||
        context.parentId !== oldContext.parentId ||
        JSON.stringify(context.filters) !== JSON.stringify(oldContext.filters));

    if (needsRefresh) {
      await this.initialize(this.context);
    } else if (context.searchQuery !== undefined) {
      // Just update search without full refresh
      this.setSearchQuery(context.searchQuery);
    }
  }

  /**
   * Set the search query and filter items
   */
  setSearchQuery(query: string): void {
    this.state.searchQuery = query;

    if (!query.trim()) {
      // If query is empty, show all items with filters applied
      this.state.filteredItems = this.applyFilters(this.state.items);
    } else {
      // Filter items based on search query
      const filtered = this.state.items.filter((item) =>
        this.filterItem(item, query)
      );
      this.state.filteredItems = this.applyFilters(filtered);
    }

    // Don't auto-focus after search
    // Focus will be set when user starts keyboard navigation
    this.state.focusedItem = null;
  }

  /**
   * Get all filtered items
   */
  getItems(): T[] {
    return this.state.filteredItems;
  }

  /**
   * Get selected items from the independent cache
   * Returns items from cache, not from state.selectedItems
   */
  getSelectedItems(): T[] {
    return Array.from(this.selectedItemsCache.values());
  }

  /**
   * Get the currently focused item
   */
  getFocusedItem(): T | null {
    return this.state.focusedItem;
  }

  /**
   * Get total item count
   */
  getItemCount(): number {
    return this.state.filteredItems.length;
  }

  /**
   * Focus the first item
   */
  focusFirst(): void {
    if (this.state.filteredItems.length > 0) {
      this.state.focusedItem = this.state.filteredItems[0];
    }
  }

  /**
   * Focus the last item
   */
  focusLast(): void {
    if (this.state.filteredItems.length > 0) {
      this.state.focusedItem =
        this.state.filteredItems[this.state.filteredItems.length - 1];
    }
  }

  /**
   * Focus a specific item
   */
  focusItem(item: T): void {
    // Verify the item exists in filtered items
    const exists = this.state.filteredItems.some(
      (i) => this.itemConfig.getId(i) === this.itemConfig.getId(item)
    );

    if (exists) {
      this.state.focusedItem = item;
    }
  }

  /**
   * Select an item (mode-aware)
   * Uses independent selection tracking to persist across navigation
   */
  selectItem(item: T): void {
    const itemId = this.itemConfig.getId(item);

    // Check if item is selectable
    if (this.itemConfig.isSelectable && !this.itemConfig.isSelectable(item)) {
      return;
    }

    if (this.selectionMode === 'single') {
      // Single mode: replace selection
      this.selectedIds.clear();
      this.selectedItemsCache.clear();
      this.selectedIds.add(itemId);
      this.selectedItemsCache.set(itemId, item);
    } else {
      // Multi mode: add to selection if not already selected
      if (!this.selectedIds.has(itemId)) {
        this.selectedIds.add(itemId);
        this.selectedItemsCache.set(itemId, item);
      }
    }

    // Sync state.selectedItems for backward compatibility
    this.syncSelectedItemsState();
  }

  /**
   * Toggle item selection (multi-mode only)
   * Uses independent selection tracking to persist across navigation
   */
  toggleItem(item: T): void {
    if (this.selectionMode === 'single') {
      // In single mode, just select the item
      this.selectItem(item);
      return;
    }

    const itemId = this.itemConfig.getId(item);

    if (this.selectedIds.has(itemId)) {
      // Remove from selection
      this.selectedIds.delete(itemId);
      this.selectedItemsCache.delete(itemId);
    } else {
      // Add to selection
      if (this.itemConfig.isSelectable && !this.itemConfig.isSelectable(item)) {
        return;
      }
      this.selectedIds.add(itemId);
      this.selectedItemsCache.set(itemId, item);
    }

    // Sync state.selectedItems for backward compatibility
    this.syncSelectedItemsState();
  }

  /**
   * Clear all selections
   * Clears both independent cache and state
   */
  clearSelection(): void {
    this.selectedIds.clear();
    this.selectedItemsCache.clear();
    this.state.selectedItems = [];
  }

  /**
   * Set selected items by IDs
   * Efficiently syncs selection to match the provided IDs
   */
  setSelectedIds(ids: string[] | Set<string>): void {
    const targetIds = new Set(Array.isArray(ids) ? ids : ids);

    // In single mode, only select the first ID
    if (this.selectionMode === 'single') {
      this.selectedIds.clear();
      this.selectedItemsCache.clear();
      if (targetIds.size > 0) {
        const firstId = Array.from(targetIds)[0];
        // Find the item with this ID
        const item = this.state.items.find(
          (item) => this.itemConfig.getId(item) === firstId
        );
        if (item) {
          this.selectItem(item);
        }
      }
      return;
    }

    // Multi mode: sync selections
    // Find IDs to add
    const toAdd: string[] = [];
    targetIds.forEach((id) => {
      if (!this.selectedIds.has(id)) {
        toAdd.push(id);
      }
    });

    // Find IDs to remove
    const toRemove: string[] = [];
    this.selectedIds.forEach((id) => {
      if (!targetIds.has(id)) {
        toRemove.push(id);
      }
    });

    // Remove unselected items
    toRemove.forEach((id) => {
      this.selectedIds.delete(id);
      this.selectedItemsCache.delete(id);
    });

    // Add new selections
    toAdd.forEach((id) => {
      const item = this.state.items.find(
        (item) => this.itemConfig.getId(item) === id
      );
      if (item) {
        this.selectedIds.add(id);
        this.selectedItemsCache.set(id, item);
      }
    });

    // Sync state
    this.syncSelectedItemsState();
  }

  /**
   * Navigate to parent (for hierarchical structures)
   */
  navigateToParent?(): void {
    if (this.navigationStack.length > 0) {
      this.currentParent = this.navigationStack.pop() ?? null;
      this.refreshItemsForCurrentContext();
    }
  }

  /**
   * Navigate to child (for hierarchical structures)
   * Subclasses should call setNavigationTarget() before this
   */
  navigateToChild?(): void {
    if (this.pendingNavigationTarget !== undefined) {
      this.navigationStack.push(this.currentParent);
      this.currentParent = this.pendingNavigationTarget;
      this.pendingNavigationTarget = undefined;
      this.refreshItemsForCurrentContext();
    }
  }

  /**
   * Set the target for navigation (used before navigateToChild)
   * This is a helper method for hierarchical navigation
   */
  protected setNavigationTarget(targetId: string | number | null): void {
    this.pendingNavigationTarget = targetId;
  }

  /**
   * Get current navigation context
   */
  getCurrentContext(): BrowserContext {
    return {
      mode: this.selectionMode,
      searchQuery: this.state.searchQuery,
      includeArchived: this.context.includeArchived,
      parentId:
        this.currentParent !== null ? String(this.currentParent) : undefined,
      filters: this.context.filters,
    };
  }

  /**
   * Load more items (for pagination)
   */
  async loadMore(): Promise<void> {
    if (!this.state.hasMore || this.state.isLoading) {
      return;
    }

    this.state.isLoading = true;

    try {
      this.state.page++;
      const newItems = await this.loadPage(this.state.page);

      // Append new items
      this.state.items = [...this.state.items, ...newItems];

      // Reapply filters
      if (this.state.searchQuery) {
        this.setSearchQuery(this.state.searchQuery);
      } else {
        this.state.filteredItems = this.applyFilters(this.state.items);
      }

      // Check if there are more items
      this.state.hasMore = await this.checkHasMore();
    } catch (error) {
      this.state.error =
        error instanceof Error ? error.message : 'Failed to load more items';
      console.error('Failed to load more items:', error);
    } finally {
      this.state.isLoading = false;
    }
  }

  /**
   * Get the current state (useful for debugging and external state access)
   */
  getState(): IBrowserState<T> {
    return { ...this.state };
  }

  /**
   * Get reactive state refs for Vue integration
   * This allows the composable to access reactive state without polling
   */
  getReactiveState() {
    return toRefs(this.reactiveState);
  }

  /**
   * Check if an item is selected
   * Checks the independent selection Set for immediate, accurate state
   */
  isItemSelected(item: T): boolean {
    const itemId = this.itemConfig.getId(item);
    return this.selectedIds.has(itemId);
  }

  /**
   * Check if an item is focused
   */
  isItemFocused(item: T): boolean {
    if (!this.state.focusedItem) return false;
    return (
      this.itemConfig.getId(item) ===
      this.itemConfig.getId(this.state.focusedItem)
    );
  }

  /**
   * Sync state.selectedItems with independent selection cache
   * This ensures Vue's reactivity system sees the changes
   */
  private syncSelectedItemsState(): void {
    this.state.selectedItems = Array.from(this.selectedItemsCache.values());
  }

  // ============================================================
  // Protected methods for subclass extension
  // ============================================================

  /**
   * Load items from data source
   * Subclasses MUST implement this method
   */
  protected abstract loadItems(): Promise<T[]>;

  /**
   * Filter an item based on search query
   * Subclasses should override for custom filtering logic
   */
  protected filterItem(item: T, query: string): boolean {
    // Default implementation: search in display name
    const displayName = this.itemConfig.getDisplayName(item);
    const searchLower = query.toLowerCase();

    // Check display name
    if (displayName.toLowerCase().includes(searchLower)) {
      return true;
    }

    // Check secondary text if available
    if (this.itemConfig.getSecondaryText) {
      const secondaryText = this.itemConfig.getSecondaryText(item);
      if (secondaryText && secondaryText.toLowerCase().includes(searchLower)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Sort items
   * Subclasses can override for custom sorting
   */
  protected sortItems(items: T[]): T[] {
    // Default: sort alphabetically by display name
    return items.sort((a, b) => {
      const aName = this.itemConfig.getDisplayName(a).toLowerCase();
      const bName = this.itemConfig.getDisplayName(b).toLowerCase();
      return aName.localeCompare(bName);
    });
  }

  /**
   * Apply additional filters (archived, disabled, etc.)
   */
  protected applyFilters(items: T[]): T[] {
    let filtered = [...items];

    // Filter out archived items unless explicitly included
    if (!this.context.includeArchived && this.itemConfig.isArchived) {
      filtered = filtered.filter((item) => !this.itemConfig.isArchived!(item));
    }

    // Apply custom filters from context
    if (this.context.filters) {
      filtered = this.applyCustomFilters(filtered, this.context.filters);
    }

    // Sort the filtered items
    return this.sortItems(filtered);
  }

  /**
   * Apply custom filters from context
   * Subclasses can override for entity-specific filtering
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected applyCustomFilters(items: T[], _filters: ModalFilters): T[] {
    // Default: no additional filtering
    // Subclasses should override this method to use the filters parameter
    return items;
  }

  /**
   * Load a specific page of items
   * Subclasses should override for pagination support
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected async loadPage(_page: number): Promise<T[]> {
    // Default: no pagination, return empty array
    // Subclasses should override this method to use the page parameter
    return [];
  }

  /**
   * Check if there are more items to load
   * Subclasses should override for pagination support
   */
  protected async checkHasMore(): Promise<boolean> {
    // Default: no more items
    return false;
  }

  /**
   * Get parent of an item (for hierarchical structures)
   * Subclasses should override for hierarchical support
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected getItemParent(_item: T): T | null {
    // Default: no parent
    // Subclasses should override this method to use the item parameter
    return null;
  }

  /**
   * Refresh items for current navigation context
   * Used for hierarchical navigation
   */
  protected async refreshItemsForCurrentContext(): Promise<void> {
    // Save current context
    const oldContext = { ...this.context };

    // Update context with current parent
    this.context.parentId =
      this.currentParent !== null ? String(this.currentParent) : undefined;

    // Reload items
    await this.initialize(this.context);

    // Restore search query if it existed
    if (oldContext.searchQuery) {
      this.setSearchQuery(oldContext.searchQuery);
    }
  }

  /**
   * Handle item activation (e.g., double-click, Enter key)
   * Subclasses can override for custom activation behavior
   */
  protected activateItem(item: T): void {
    // Default: select the item
    this.selectItem(item);
  }

  /**
   * Validate selection
   * Subclasses can override for custom validation
   */
  protected validateSelection(): boolean {
    // Default: always valid
    return true;
  }
}
